/* eslint complexity: [1, 50] */
/* global u */

/**
 * Datepicker class.
 */
class Datepicker {
  /**
   * Constructor for datepicker.
   * @param  {Umbrella Object} id id umbrella element.
   * @param  {Umbrella Object} $btn button umbrella element.
   * @param  {boolean} modal is modal?
   */
  constructor(id, $btn, modal) {
    this.init = false;
    // The widget ID is the wrapper ID appended with '-dp1'.
    this.id = id;
    this.$id = u(`#${id}-dp1`); // element to attach widget to
    if (!this.$id.nodes.length) {
      return;
    }
    const dp_elem = this.$id.nodes[0];
    this.$monthObj = this.$id.find(`#${this.id}-month`);
    this.$prev = this.$id.find(`#${this.id}-bn_prev`);
    this.$next = this.$id.find(`#${this.id}-bn_next`);
    this.$grid = this.$id.find(`#${this.id}-cal`);
    // NB input uses a class.
    this.input = this.$id.find(`.${this.id}-input`);
    this.input = this.input.nodes.length ? this.input.nodes[0] : null;
    this.$btn = $btn; // div or text box that will receive the selected date string and focus (if modal)
    this.bModal = modal; // true if datepicker should appear in a modal dialog box.
    this.is_open = false;

    // Allow passing in pre-translated month names instead of double-handling here.
    this.monthNames = dp_elem.dataset.monthnames ? dp_elem.dataset.monthnames.split(',') : [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    this.dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];

    this.callbacks = [];

    // Date date min/max.
    // todo use these to restrict calendar navigation.
    this.min = dp_elem.dataset.min ? new Date(dp_elem.dataset.min) : new Date();
    this.max = dp_elem.dataset.max ? new Date(dp_elem.dataset.max) : new Date('now +3 years');
    this.minYear = this.min.getUTCFullYear();
    this.maxYear = this.max.getUTCFullYear();
    this.minMonth = this.min.getUTCMonth();
    this.maxMonth = this.max.getUTCMonth();
    this.minDate = this.min.getUTCDate();
    this.maxDate = this.max.getUTCDate();

    // Get initial date from hidden input value.
    // If null, it will be today's date.
    const dateString = this.input && this.input.value && this.input.value.trim() ? this.input.value : null;
    const date = dateString && !isNaN(Date.parse(dateString)) ? new Date(dateString) : new Date();
    this.setDate(date.getUTCDate(), date.getUTCMonth(), date.getUTCFullYear(), !!dateString);

    this.keys = {
      tab: 9,
      enter: 13,
      esc: 27,
      space: 32,
      pageup: 33,
      pagedown: 34,
      end: 35,
      home: 36,
      left: 37,
      up: 38,
      right: 39,
      down: 40
    };

    // update the table's activedescdendant to point to the current day
    this.$grid.attr('aria-activedescendant', this.$grid.find('.selected').attr('id'));

    this.bindHandlers();

    // hide dialog if in modal mode
    if (this.bModal === true) {
      this.$id.attr('aria-hidden', 'true');
    }

    $btn.on('click', e => {
      if (this.is_open) {
        this.hideDlg();
      } else {
        this.showDlg();
      }

      e.stopPropagation();
      e.preventDefault();
      return false;
    });

    this.init = true;
  }

  /**
   * Adds callback to be run on date selected;
   * @param  {[type]} func [description]
   */
  onDatePicked(func) {
    this.callbacks.push(func);
  }

  /**
   * Runs callback and updates target on date selected;
   * @param  {[type]} day    [description]
   * @param  {[type]} month  [description]
   * @param  {[type]} year   [description]
   */
  datePicked(month, day, year) {
    const data = {
      day: parseInt(day),
      month: parseInt(month),
      year: parseInt(year),
      day_padded: (`00${day}`).slice(-2),
      month_padded: (`00${month}`).slice(-2),
      year_padded: (`0000${year}`).slice(-4),
      month_name: this.monthNames[parseInt(month) - 1],
      prefix: this.id
    };

    // Set input value if different.
    const date_string = `${data.year_padded}-${data.month_padded}-${data.day_padded}`;
    if (date_string !== this.input.value) {
      this.input.value = date_string;

      // Trigger change on input.
      u(this.input).trigger('change');
    }

    this.callbacks.forEach(callback => callback(data));
  }


  /**
   * A member function to populate the datepicker grid with calendar days representing the current month.
   */
  popGrid() {
    const $tbody = this.$grid.find('tbody');

    let numDays = this.calcNumDays(this.year, this.month);
    let startWeekday = this.calcStartWeekday(this.year, this.month);
    let weekday = 0;
    let curDay = 1;
    let rowCount = 1;
    let gridCells = `\t<tr id="${this.id}-row0">\n`;

    // clear the grid
    $tbody.empty();
    u(`#${this.id}-errMsg`).empty();

    // Insert the leading empty cells
    for (weekday = 0; weekday < startWeekday; weekday++) {

      gridCells += '\t\t<td class="empty">&nbsp;</td>\n';
    }

    // insert the days of the month.
    for (curDay = 1; curDay <= numDays; curDay++) {

      if (curDay === this.date && this.currentDate === true) {

        gridCells += `\t\t<td id="${this.id}-day${curDay}" class="selected" headers="row${rowCount} ${this.dayNames[weekday]}" role="gridcell" aria-selected="true">${curDay}</td>`;

      } else {
        gridCells += `\t\t<td id="${this.id}-day${curDay}" headers="row${rowCount} ${this.dayNames[weekday]}" role="gridcell" aria-selected="false">${curDay}</td>`;
      }

      if (weekday === 6 && curDay < numDays) {
        // This was the last day of the week, close it out
        // and begin a new one
        gridCells += `\t</tr>\n\t<tr id="${this.id}-row${rowCount}">\n`;
        rowCount++;
        weekday = 0;
      } else {
        weekday++;
      }
    }

    // Insert any trailing empty cells
    for (weekday; weekday < 7; weekday++) {

      gridCells += '\t\t<td class="empty">&nbsp;</td>\n';
    }

    gridCells += '\t</tr>';

    $tbody.nodes[0].innerHTML = gridCells;

    // display the current month
    this.$monthObj.html(`${this.monthNames[this.month]} ${this.year}`);
  }


  /**
   * A member function to calculate the number of days in a given month.
   * @param  {int} year
   * @param  {int} month
   * @return {integer} Number of days.
   */
  calcNumDays(year, month) {
    return 32 - new Date(year, month, 32).getDate();
  }


  /**
   * A member function to calculate the day of the week the first day of a month lands on.
   * @param  {int} year
   * @param  {int} month
   * @return {integer} Number representing the day of the week (0=Sunday....6=Saturday).
   */
  calcStartWeekday(year, month) {
    return new Date(year, month, 1).getDay();
  } // end calcStartWeekday()


  /**
   * A member function to show the previous month.
   * @param  {int} offset may be used to specify an offset for setting focus on a day the specified number of days from the end of the month.
   */
  showPrevMonth(offset) {
    // show the previous month
    if (this.month === 0) {
      this.month = 11;
      this.year--;
    } else {
      this.month--;
    }

    if (this.month !== this.curMonth || this.year !== this.curYear) {
      this.currentDate = false;
    } else {
      this.currentDate = true;
    }

    // populate the calendar grid
    this.popGrid();

    this.$monthObj.html(`${this.monthNames[this.month]} ${this.year}`);

    // if offset was specified, set focus on the last day - specified offset
    if (offset) {
      let numDays = this.calcNumDays(this.year, this.month);
      let day = `${this.id}-day${(numDays - offset)}`;

      this.$grid.attr('aria-activedescendant', day);
      u(`#${day}`).addClass('focus').attr('aria-selected', 'true');
    }

  } // end showPrevMonth()


  /**
   * A member function to show the next month.
   * @param  {[type]} offset may be used to specify an offset for setting focus on a day the specified number of days from the beginning of the month.
   */
  showNextMonth(offset) {

    // show the next month
    if (this.month === 11) {
      this.month = 0;
      this.year++;
    } else {
      this.month++;
    }

    if (this.month !== this.curMonth || this.year !== this.curYear) {
      this.currentDate = false;
    } else {
      this.currentDate = true;
    }

    // populate the calendar grid
    this.popGrid();

    // if offset was specified, set focus on the first day + specified offset
    if (offset) {
      let day = `${this.id}-day${offset}`;

      this.$grid.attr('aria-activedescendant', day);
      u(`#${day}`).addClass('focus').attr('aria-selected', 'true');
    }

  } // end showNextMonth()


  /**
   * A member function to show the previous year.
   */
  showPrevYear() {

    // decrement the year
    this.year--;

    if (this.month !== this.curMonth || this.year !== this.curYear) {
      this.currentDate = false;
    } else {
      this.currentDate = true;
    }

    // populate the calendar grid
    this.popGrid();

  } // end showPrevYear()


  /**
   * A member function to show the next year.
   */
  showNextYear() {

    // increment the year
    this.year++;

    if (this.month !== this.curMonth || this.year !== this.curYear) {
      this.currentDate = false;
    } else {
      this.currentDate = true;
    }

    // populate the calendar grid
    this.popGrid();

  } // end showNextYear()

  /**
   * A member function to set the selected date.
   */
  setDate(day, month, year, updateSelection) {

    // NB month is 0-based.
    if (!day || !year || (!month && month !== 0)) {
      return;
    }

    this.curYear = year;
    this.year = this.curYear;

    this.curMonth = month;
    this.month = this.curMonth;
    this.currentDate = true;

    this.date = day;

    // populate the calendar grid
    this.popGrid();

    const cellId = `${this.id}-day${day}`;
    this.$grid.find('.focus').removeClass('focus').attr('aria-selected', 'false');
    this.$grid.find(`#${cellId}`).addClass('focus').attr('aria-selected', 'true');
    this.$grid.attr('aria-activedescendant', cellId);

    if (updateSelection) {
      // datePicked takes 1-based month.
      this.datePicked(this.month + 1, this.date, this.year);
    }
  } // end setDate()

  /**
   * A member function to unset the date.
   */
  unsetDate() {
    if (this.input.value) {
      this.input.value = '';

      // Trigger change on input.
      u(this.input).trigger('change');
    }
  }

  /**
   * A member function to bind event handlers for the widget.
   */
  bindHandlers() {
    const thisObj = this;

    /* bind button handlers */

    this.$prev.on('click', function(e) {
      return thisObj.handlePrevClick(e);
    });

    this.$next.on('click', function(e) {
      return thisObj.handleNextClick(e);
    });

    this.$prev.on('keydown', function(e) {
      return thisObj.handlePrevKeyDown(e);
    });

    this.$next.on('keydown', function(e) {
      return thisObj.handleNextKeyDown(e);
    });

    /* bind grid handlers */

    this.$grid.on('keydown', function(e) {
      return thisObj.handleGridKeyDown(e);
    });

    this.$grid.on('keypress', function(e) {
      return thisObj.handleGridKeyPress(e);
    });

    this.$grid.on('focus', function(e) {
      return thisObj.handleGridFocus(e);
    });

    this.$grid.on('blur', function(e) {
      return thisObj.handleGridBlur(e);
    });

    this.$grid.nodes[0].addEventListener('click', function(e) {
      if (e.target.nodeName === 'TD') {
        return thisObj.handleGridClick(this, e);
      }
    });

  } // end bindHandlers();


  /**
   * A member function to process click events for the prev month button.
   * @param  {object} e event object.
   * @return {boolean} false if consuming event, true if propagating
   **/
  handlePrevClick(e) {
    const active = this.$grid.attr('aria-activedescendant');

    if (e.altKey) {
      this.showPrevYear();
    } else {
      this.showPrevMonth();
    }

    if (u(`#${active}`).attr('id') === undefined) {
      let lastDay = `day${this.calcNumDays(this.year, this.month)}`;
      u(`#${this.id}-${lastDay}`).addClass('focus').attr('aria-selected', 'true');
    } else {
      u(`#${active}`).addClass('focus').attr('aria-selected', 'true');
    }

    e.stopPropagation();
    e.preventDefault();
    return false;

  } // end handlePrevClick()


  /**
   * A member function to process click events for the next month button.
   * @param  {object} e event object.
   * @return {boolean} false if consuming event, true if propagating
   **/
  handleNextClick(e) {
    const active = this.$grid.attr('aria-activedescendant');

    if (e.altKey) {
      this.showNextYear();
    } else {
      this.showNextMonth();
    }

    if (u(`#${active}`).attr('id') === undefined) {
      let lastDay = `day${this.calcNumDays(this.year, this.month)}`;
      u(`#${this.id}-${lastDay}`).addClass('focus').attr('aria-selected', 'true');
    } else {
      u(`#${active}`).addClass('focus').attr('aria-selected', 'true');
    }

    e.stopPropagation();
    e.preventDefault();
    return false;

  } // end handleNextClick()


  /**
   * A member function to process keydown events for the prev month button.
   * @param  {object} e event object.
   * @return {boolean} false if consuming event, true if propagating
   **/
  handlePrevKeyDown(e) {
    switch (e.keyCode) {
      case this.keys.tab: {
        if (this.bModal === false || !e.shiftKey || e.ctrlKey) {
          return true;
        }

        this.$grid.nodes[0].focus();
        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.enter:
      case this.keys.space: {
        if (e.shiftKey) {
          return true;
        }

        if (e.altKey) {
          this.showPrevYear();
        } else {
          this.showPrevMonth();
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    }

    return true;

  } // end handlePrevKeyDown()


  /**
   * A member function to process keydown events for the next month button.
   * @param  {object} e event object.
   * @return {boolean} false if consuming event, true if propagating
   **/
  handleNextKeyDown(e) {
    switch (e.keyCode) {
      case this.keys.enter:
      case this.keys.space: {

        if (e.altKey) {
          this.showNextYear();
        } else {
          this.showNextMonth();
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    }

    return true;

  } // end handleNextKeyDown()


  /**
   * A member function to process keydown events for the datepicker grid.
   * @param  {object} e event object.
   * @return {boolean} false if consuming event, true if propagating
   **/
  handleGridKeyDown(e) {
    const $curDay = u(`#${this.$grid.attr('aria-activedescendant')}`);
    const $days = this.$grid.find('td').not('.empty');
    const $curRow = $curDay.parent();

    switch (e.keyCode) {
      case this.keys.tab: {

        if (this.bModal === true) {
          if (e.shiftKey) {
            this.$next.nodes[0].focus();
          } else {
            this.$prev.nodes[0].focus();
          }
          e.stopPropagation();
          e.preventDefault();
          return false;
        }
        break;
      }


      case this.keys.enter:
      case this.keys.space: {

        if (e.ctrlKey) {
          return true;
        }

        // update the target box
        this.datePicked(this.month + 1, $curDay.text(), this.year);


        // fall through
      }


      case this.keys.esc: {
        // dismiss the dialog box
        this.hideDlg();

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.left: {

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        let dayIndex = $days.nodes.indexOf($curDay.nodes[0]) - 1;
        let $prevDay = null;

        if (dayIndex >= 0) {
          // $prevDay = $days.eq(dayIndex);
          $prevDay = u($days.nodes[dayIndex]);

          $curDay.removeClass('focus').attr('aria-selected', 'false');
          $prevDay.addClass('focus').attr('aria-selected', 'true');

          this.$grid.attr('aria-activedescendant', $prevDay.attr('id'));
        } else {
          this.showPrevMonth(0);
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.right: {

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        let dayIndex = $days.nodes.indexOf($curDay.nodes[0]) + 1;

        // let dayIndex = $days.index($curDay) + 1;
        let $nextDay = null;

        if (dayIndex < $days.length) {
          // $nextDay = $days.eq(dayIndex);
          $nextDay = u($days.nodes[dayIndex]);
          $curDay.removeClass('focus').attr('aria-selected', 'false');
          $nextDay.addClass('focus').attr('aria-selected', 'true');

          this.$grid.attr('aria-activedescendant', $nextDay.attr('id'));
        } else {
          // move to the next month
          this.showNextMonth(1);
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.up: {

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        let dayIndex = $days.nodes.indexOf($curDay.nodes[0]) - 7;
        let $prevDay = null;

        if (dayIndex >= 0) {
          // $prevDay = $days.eq(dayIndex);
          $prevDay = u($days.nodes[dayIndex]);

          $curDay.removeClass('focus').attr('aria-selected', 'false');
          $prevDay.addClass('focus').attr('aria-selected', 'true');

          this.$grid.attr('aria-activedescendant', $prevDay.attr('id'));
        } else {
          // move to appropriate day in previous month
          dayIndex = 6 - $days.nodes.indexOf($curDay.nodes[0]);

          this.showPrevMonth(dayIndex);
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.down: {

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        let dayIndex = $days.nodes.indexOf($curDay.nodes[0]) + 7;
        let $prevDay = null;

        if (dayIndex < $days.length) {
          // $prevDay = $days.eq(dayIndex);
          $prevDay = u($days.nodes[dayIndex]);

          $curDay.removeClass('focus').attr('aria-selected', 'false');
          $prevDay.addClass('focus').attr('aria-selected', 'true');

          this.$grid.attr('aria-activedescendant', $prevDay.attr('id'));
        } else {
          // move to appropriate day in next month
          dayIndex = 8 - ($days.length - $days.nodes.indexOf($curDay.nodes[0]));

          this.showNextMonth(dayIndex);
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.pageup: {
        let active = this.$grid.attr('aria-activedescendant');

        if (e.shiftKey) {
          return true;
        }

        if (e.altKey) {
          this.showNextYear();
        } else {
          this.showNextMonth();
        }

        if (u(`#${active}`).attr('id') === undefined) {
          let lastDay = `day${this.calcNumDays(this.year, this.month)}`;
          u(`#${this.id}-${lastDay}`).addClass('focus').attr('aria-selected', 'true');
        } else {
          u(`#${active}`).addClass('focus').attr('aria-selected', 'true');
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.pagedown: {
        let active = this.$grid.attr('aria-activedescendant');

        if (e.shiftKey) {
          return true;
        }

        if (e.altKey) {
          this.showPrevYear();
        } else {
          this.showPrevMonth();
        }

        if (u(`#${active}`).attr('id') === undefined) {
          let lastDay = `day${this.calcNumDays(this.year, this.month)}`;
          u(`#${this.id}-${lastDay}`).addClass('focus').attr('aria-selected', 'true');
        } else {
          u(`#${active}`).addClass('focus').attr('aria-selected', 'true');
        }

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.home: {

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        // make sure it jumps to the first td that is not empty
        const non_empty_td = $curRow.find('td').nodes.filter(node => {
          return !node.classList.contains('empty');
        });

        const $curRowTDs = u(non_empty_td);

        const $first = u($curRowTDs.first());

        $curDay.removeClass('focus').attr('aria-selected', 'false');

        $first.addClass('focus').attr('aria-selected', 'true');

        this.$grid.attr('aria-activedescendant', $first.attr('id'));

        e.stopPropagation();
        e.preventDefault();
        return false;
      }


      case this.keys.end: {
        // make sure it jumps to the last td that is not empty
        const non_empty_td = $curRow.find('td').nodes.filter(node => {
          return !node.classList.contains('empty');
        });
        const $curRowTDs = u(non_empty_td);
        const $last = u($curRowTDs.last());

        if (e.ctrlKey || e.shiftKey) {
          return true;
        }

        $curDay.removeClass('focus').attr('aria-selected', 'false');
        $last.addClass('focus').attr('aria-selected', 'true');
        this.$grid.attr('aria-activedescendant', $last.attr('id'));

        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    }

    return true;

  } // end handleGridKeyDown()


  /**
   * A member function to consume keypress events for browsers that use keypress to scroll the screen and manipulate tabs.
   * @param  {object} e is the event object associated with the event.
   * @return {boolean} false if consuming event, true if propagating
   */
  handleGridKeyPress(e) {

    switch (e.keyCode) {
      case this.keys.tab:
      case this.keys.enter:
      case this.keys.space:
      case this.keys.esc:
      case this.keys.left:
      case this.keys.right:
      case this.keys.up:
      case this.keys.down:
      case this.keys.pageup:
      case this.keys.pagedown:
      case this.keys.home:
      case this.keys.end: {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    }

    return true;

  } // end handleGridKeyPress()


  /**
   * A a member function to process mouse click events for the datepicker grid.
   * @param  {object} id is the id of the object triggering the event.
   * @param  {object} e is the event object associated with the event.
   * @return {boolean} false if consuming event, true if propagating.
   */
  handleGridClick(id, e) {
    const $cell = u(e.target);
    let $curDay;

    if ($cell.is('.empty')) {
      return true;
    }

    this.$grid.find('.focus').removeClass('focus').attr('aria-selected', 'false');
    $cell.addClass('focus').attr('aria-selected', 'true');
    this.$grid.attr('aria-activedescendant', $cell.attr('id'));

    $curDay = u(`#${this.$grid.attr('aria-activedescendant')}`);

    // update the target box
    this.datePicked(this.month + 1, $curDay.text(), this.year);

    // dismiss the dialog box
    this.hideDlg();

    e.stopPropagation();
    e.preventDefault();
    return false;

  } // end handleGridClick()


  /**
   * A member function to process focus events for the datepicker grid.
   */
  handleGridFocus() {
    const active = this.$grid.attr('aria-activedescendant');

    if (u(`#${active}`).attr('id') === undefined) {
      let lastDay = `${this.id}-day${this.calcNumDays(this.year, this.month)}`;
      u(`#${lastDay}`).addClass('focus').attr('aria-selected', 'true');
    } else {
      u(`#${active}`).addClass('focus').attr('aria-selected', 'true');
    }

    return true;

  } // end handleGridFocus()


  /**
   * A member function to process blur events for the datepicker grid.
   */
  handleGridBlur() {
    u(`#${this.$grid.attr('aria-activedescendant')}`).removeClass('focus').attr('aria-selected', 'false');
    return true;
  } // end handleGridBlur()


  /**
   * A  member function to show the datepicker and give it focus. This function is only called if the datepicker is used in modal dialog mode
   */
  showDlg() {
    const that = this;
    this.is_open = true;

    // show the dialog
    this.$id.attr('aria-hidden', 'false');
    this.$grid.nodes[0].focus();

    /**
     * Delegated click event handler.
     * @param  {Event} e
     */
    function delegatedClose(e) {
      if (!that.$id.nodes[0].contains(e.target)) {
        // if the event target is the button stop propagation and default so that it doesn't open the calendar again
        e.stopImmediatePropagation();
        e.preventDefault();

        that.hideDlg();
      }
    }

    document.addEventListener('click', delegatedClose, true);

    this.delegationRemoval = () => document.removeEventListener('click', delegatedClose, true);


  } // end showDlg()


  /**
   * A member function to hide the datepicker and remove focus. This function is only called if the datepicker is used in modal dialog mode.
   */
  hideDlg() {
    if (this.delegationRemoval) {
      this.delegationRemoval();
      delete this.delegationRemoval;
    }

    // hide the dialog
    this.$id.attr('aria-hidden', 'true');

    // set focus on the focus target
    this.is_open = false;
  } // end showDlg()
}


export default Datepicker;
